import { PALETTE_MAGENTA } from '@shared/builtinPulseMaps/palettes';
import { buildBuiltinAudio, selectBeats } from '@shared/builtinPulseMaps/audioFromOnsets';
import onAndOnOnsets from '@shared/builtinPulseMaps/audio/on-and-on.onsets.json' with { type: 'json' };
import {
  OSU_PLAYFIELD_HEIGHT,
  OSU_PLAYFIELD_WIDTH,
  type CircleHitObject,
  type CirclesChart,
} from '@shared/circlesChart';

const TUTORIAL_TOTAL_BEATS = 32;

/**
 * Hand-crafted teaching chart for `/circles/tutorial`. Same audio/BPM
 * as the Pulse tutorial (slowest builtin so a brand-new player has
 * room to react), with circles arranged in a deterministic, easy-to-
 * read pattern: every required tap is on a real audio onset (so the
 * music ↔ click feedback is honest) and the on-screen layout walks
 * the cursor in a slow zig-zag across the playfield.
 *
 * No spinners, no 4-1 fast streams, no overlapping clusters — the
 * goal is "you click here, then there" with maximum legibility.
 */
export function buildTutorialCirclesChart(): CirclesChart {
  const audio = buildBuiltinAudio({
    src: '/audio/tracks/on-and-on.mp3',
    attribution:
      'Tutorial uses "On & On" by Cartoon (feat. Daniel Levi) [NCS Release] - https://ncs.io/onandon',
    data: onAndOnOnsets,
  });

  // Pull strong onsets in two batches: a sparse intro then a slightly
  // denser chorus stanza, mirroring the Pulse tutorial's pacing.
  const introBeats = selectBeats(onAndOnOnsets, {
    fromBeat: 4,
    untilBeat: 14,
    minIntensity: 0.3,
    minSpacing: 2,
  }).slice(0, 4);
  const chorusBeats = selectBeats(onAndOnOnsets, {
    fromBeat: 14,
    untilBeat: TUTORIAL_TOTAL_BEATS - 2,
    minIntensity: 0.25,
    minSpacing: 1.5,
  }).slice(0, 8);

  // Five waypoints walked in a horizontal zig-zag across the
  // playfield. Cycling through this list gives a calm, predictable
  // path the player can follow with the cursor without big jumps.
  const waypoints: Array<{ x: number; y: number }> = [
    { x: OSU_PLAYFIELD_WIDTH * 0.25, y: OSU_PLAYFIELD_HEIGHT * 0.5 },
    { x: OSU_PLAYFIELD_WIDTH * 0.45, y: OSU_PLAYFIELD_HEIGHT * 0.35 },
    { x: OSU_PLAYFIELD_WIDTH * 0.55, y: OSU_PLAYFIELD_HEIGHT * 0.55 },
    { x: OSU_PLAYFIELD_WIDTH * 0.75, y: OSU_PLAYFIELD_HEIGHT * 0.4 },
    { x: OSU_PLAYFIELD_WIDTH * 0.6, y: OSU_PLAYFIELD_HEIGHT * 0.65 },
  ];

  const allBeats = [...introBeats, ...chorusBeats];
  const hitObjects: CircleHitObject[] = allBeats.map((beat, i) => {
    const wp = waypoints[i % waypoints.length];
    return {
      id: `t${i}`,
      beat,
      x: wp.x,
      y: wp.y,
      // New combo every 4 hits so the renderer cycles through palette
      // colours and the player can see a "fresh group" cue.
      newCombo: i % 4 === 0,
      comboIndex: i % 4,
      kind: 'circle',
    };
  });

  return {
    id: 'tutorial-circles',
    slug: 'tutorial-circles',
    title: 'How to play (Circles)',
    description:
      'Quick teaching chart on a slow track. Click each circle as the approach ring meets it.',
    mode: 'circles',
    bpm: onAndOnOnsets.bpm,
    totalBeats: TUTORIAL_TOTAL_BEATS,
    difficulty: 1,
    variant: 'normal',
    palette: PALETTE_MAGENTA,
    audio,
    hitObjects,
    // Generous approach window so a brand-new player has time to read
    // the ring; tighter than this is fine once they're warmed up.
    // approachMs is BPM-independent (osu AR semantics); approachBeats
    // is the schema-required fallback derived from the same value.
    approachMs: 1500,
    approachBeats: (1500 * onAndOnOnsets.bpm) / 60_000,
    circleRadius: 50,
  };
}

const SLIDER_TUTORIAL_TOTAL_BEATS = 28;

/**
 * Hand-crafted teaching chart for `/circles/tutorial/slider`. Played the
 * first time a player is about to enter a circles tier that contains
 * sliders (hard / extreme / insane) — the slider-free `normal` tier
 * never triggers it. Same slow track + generous approach window as the
 * click tutorial so the new mechanic, not the speed, is the lesson.
 *
 * Two warm-up clicks, then three deliberately slow + well-spaced
 * sliders: two single slides (click the head, trace the ball to the
 * tail) and one reverse slider (follow the arrow back to the head). All
 * straight `L` curves for maximum legibility.
 */
export function buildSliderTutorialCirclesChart(): CirclesChart {
  const audio = buildBuiltinAudio({
    src: '/audio/tracks/on-and-on.mp3',
    attribution:
      'Tutorial uses "On & On" by Cartoon (feat. Daniel Levi) [NCS Release] - https://ncs.io/onandon',
    data: onAndOnOnsets,
  });

  const hitObjects: CircleHitObject[] = [
    { id: 'st-warm-0', beat: 4, x: 150, y: 200, newCombo: true, comboIndex: 0, kind: 'circle' },
    { id: 'st-warm-1', beat: 6, x: 360, y: 200, newCombo: false, comboIndex: 0, kind: 'circle' },
    // Slider 1 — straight left→right over two beats.
    {
      id: 'st-slide-0',
      beat: 8,
      x: 120,
      y: 170,
      newCombo: true,
      comboIndex: 1,
      kind: 'slider',
      slider: {
        curveType: 'L',
        controlPoints: [{ x: 320, y: 170 }],
        length: 200,
        slides: 1,
        endBeat: 10,
        tickBeats: [],
        repeatBeats: [],
      },
    },
    // Slider 2 — gentle diagonal over two beats.
    {
      id: 'st-slide-1',
      beat: 12,
      x: 380,
      y: 130,
      newCombo: false,
      comboIndex: 1,
      kind: 'slider',
      slider: {
        curveType: 'L',
        controlPoints: [{ x: 200, y: 260 }],
        length: 222,
        slides: 1,
        endBeat: 14,
        tickBeats: [],
        repeatBeats: [],
      },
    },
    // Slider 3 — reverse slider: head→tail→head, with the bounce at the
    // midpoint so the player learns to follow the reverse arrow.
    {
      id: 'st-slide-2',
      beat: 16,
      x: 170,
      y: 280,
      newCombo: true,
      comboIndex: 2,
      kind: 'slider',
      slider: {
        curveType: 'L',
        controlPoints: [{ x: 370, y: 280 }],
        length: 200,
        slides: 2,
        endBeat: 20,
        tickBeats: [],
        repeatBeats: [18],
      },
    },
    { id: 'st-end', beat: 23, x: 256, y: 170, newCombo: true, comboIndex: 3, kind: 'circle' },
  ];

  return {
    id: 'tutorial-circles-slider',
    slug: 'tutorial-circles-slider',
    title: 'How to play sliders (Circles)',
    description:
      'Click the slider head, then hold and trace the ball along the path to the tail.',
    mode: 'circles',
    bpm: onAndOnOnsets.bpm,
    totalBeats: SLIDER_TUTORIAL_TOTAL_BEATS,
    difficulty: 1,
    // The first tier that contains sliders is `hard`; tag the teaching
    // chart to match so any variant-aware tuning reads as that tier.
    variant: 'hard',
    palette: PALETTE_MAGENTA,
    audio,
    hitObjects,
    approachMs: 1500,
    approachBeats: (1500 * onAndOnOnsets.bpm) / 60_000,
    circleRadius: 50,
    sliderMultiplier: 1.4,
    sliderTickRate: 1,
  };
}
